import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createStorePromotionalAd,
  getStorePromotionalAd,
  listPromotionalAdDestinationCategories,
  listPromotionalAdDestinationProducts,
  listStorePromotionalAds,
  mapPromotionalAdError,
  reorderStorePromotionalAds,
  softDeleteStorePromotionalAd,
  updateStorePromotionalAd,
} from '../services/promotional-ad.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  CreateStorePromotionalAdInput,
  DeleteStorePromotionalAdInput,
  PromotionalAdDestinationCategoryOption,
  PromotionalAdDestinationProductOption,
  PromotionalAdErrorKey,
  ReorderStorePromotionalAdsInput,
  StorePromotionalAdStoreContext,
  StorePromotionalAdWithPreview,
  UpdateStorePromotionalAdInput,
} from '../types/promotional-ad';

export const promotionalAdKeys = {
  all: ['store-promotional-ads'] as const,
  list: (storeId: string) => ['store-promotional-ads', 'list', storeId] as const,
  detail: (storeId: string, adId: string) =>
    ['store-promotional-ads', 'detail', storeId, adId] as const,
  destinationCategories: (storeId: string, search: string) =>
    ['store-promotional-ads', 'destinations', 'categories', storeId, search] as const,
  destinationProducts: (storeId: string, search: string, categoryId: string | null) =>
    ['store-promotional-ads', 'destinations', 'products', storeId, search, categoryId] as const,
};

export interface UseStorePromotionalAdsResult extends Pick<
  UseQueryResult<StorePromotionalAdWithPreview[]>,
  'data' | 'isLoading' | 'isError' | 'error' | 'refetch'
> {
  readonly ads: StorePromotionalAdWithPreview[];
  readonly errorKey: PromotionalAdErrorKey | null;
}

export function useStorePromotionalAds(
  client: SupabaseClient,
  store: StorePromotionalAdStoreContext | null,
): UseStorePromotionalAdsResult {
  const q = useQuery({
    queryKey: store ? promotionalAdKeys.list(store.storeId) : promotionalAdKeys.all,
    queryFn: () => listStorePromotionalAds(client, store as StorePromotionalAdStoreContext),
    enabled: !!store,
    staleTime: 60_000,
  });

  return {
    data: q.data,
    ads: q.data ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
    errorKey: q.error ? mapPromotionalAdError(q.error) : null,
  };
}

export function useStorePromotionalAd(
  client: SupabaseClient,
  args: { readonly store: StorePromotionalAdStoreContext | null; readonly adId: string | null },
): UseQueryResult<StorePromotionalAdWithPreview | null> {
  return useQuery({
    queryKey:
      args.store && args.adId
        ? promotionalAdKeys.detail(args.store.storeId, args.adId)
        : promotionalAdKeys.all,
    queryFn: () =>
      getStorePromotionalAd(client, {
        store_id: args.store?.storeId ?? '',
        store_slug: args.store?.storeSlug ?? '',
        ad_id: args.adId ?? '',
      }),
    enabled: !!args.store && !!args.adId,
    staleTime: 60_000,
  });
}

export function usePromotionalAdDestinationCategories(
  client: SupabaseClient,
  args: { readonly storeId: string | null; readonly search: string },
): UseQueryResult<PromotionalAdDestinationCategoryOption[]> {
  const search = args.search.trim();
  return useQuery({
    queryKey: args.storeId
      ? promotionalAdKeys.destinationCategories(args.storeId, search)
      : promotionalAdKeys.all,
    queryFn: () =>
      listPromotionalAdDestinationCategories(client, {
        store_id: args.storeId ?? '',
        search,
      }),
    enabled: !!args.storeId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePromotionalAdDestinationProducts(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
    readonly categoryId?: string | null;
  },
): UseQueryResult<PromotionalAdDestinationProductOption[]> {
  const search = args.search.trim();
  const categoryId = args.categoryId ?? null;
  return useQuery({
    queryKey: args.storeId
      ? promotionalAdKeys.destinationProducts(args.storeId, search, categoryId)
      : promotionalAdKeys.all,
    queryFn: () =>
      listPromotionalAdDestinationProducts(client, {
        store_id: args.storeId ?? '',
        search,
        category_id: categoryId,
      }),
    enabled: !!args.storeId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCreatePromotionalAd(
  client: SupabaseClient,
): UseMutationResult<StorePromotionalAdWithPreview, unknown, CreateStorePromotionalAdInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createStorePromotionalAd(client, input),
    onSuccess: (_ad, input) => {
      void queryClient.invalidateQueries({ queryKey: promotionalAdKeys.list(input.store_id) });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function useUpdatePromotionalAd(
  client: SupabaseClient,
): UseMutationResult<StorePromotionalAdWithPreview, unknown, UpdateStorePromotionalAdInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateStorePromotionalAd(client, input),
    onSuccess: (ad) => {
      void queryClient.invalidateQueries({ queryKey: promotionalAdKeys.list(ad.store_id) });
      void queryClient.invalidateQueries({
        queryKey: promotionalAdKeys.detail(ad.store_id, ad.id),
      });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function useReorderPromotionalAds(
  client: SupabaseClient,
): UseMutationResult<
  void,
  unknown,
  ReorderStorePromotionalAdsInput,
  { readonly previousAds?: StorePromotionalAdWithPreview[] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => reorderStorePromotionalAds(client, input),
    onMutate: async (input) => {
      const listKey = promotionalAdKeys.list(input.store_id);
      await queryClient.cancelQueries({ queryKey: listKey });
      const previousAds = queryClient.getQueryData<StorePromotionalAdWithPreview[]>(listKey);

      if (previousAds) {
        const adsById = new Map(previousAds.map((ad) => [ad.id, ad]));
        const orderedAds = input.ordered_ad_ids
          .map((id) => adsById.get(id) ?? null)
          .filter((ad): ad is StorePromotionalAdWithPreview => ad !== null)
          .map((ad, index) => ({ ...ad, display_order: index }));
        const orderedIds = new Set(input.ordered_ad_ids);
        const remainingAds = previousAds
          .filter((ad) => !orderedIds.has(ad.id))
          .map((ad, index) => ({ ...ad, display_order: orderedAds.length + index }));

        queryClient.setQueryData<StorePromotionalAdWithPreview[]>(listKey, [
          ...orderedAds,
          ...remainingAds,
        ]);
      }

      return { previousAds };
    },
    onError: (_err, input, context) => {
      if (context?.previousAds) {
        queryClient.setQueryData(promotionalAdKeys.list(input.store_id), context.previousAds);
      }
    },
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: promotionalAdKeys.list(input.store_id) });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}

export function useDeletePromotionalAd(
  client: SupabaseClient,
): UseMutationResult<void, unknown, DeleteStorePromotionalAdInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => softDeleteStorePromotionalAd(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: promotionalAdKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
    },
  });
}
